import { request } from 'api/request';
import { CompanyUserMinimal } from 'api/types';

export type ReviewSeverity = 'must_fix' | 'review_recommended' | 'improvement';

export type ReviewFindingCategory =
  | 'inactive_trigger'
  | 'dead_end_branch'
  | 'weak_content'
  | 'unused_attribute'
  | 'unused_output';

export type ReviewTargetKind = 'action' | 'trigger' | 'branch' | 'field';

/**
 * What kind of problem a finding reports, as opposed to how it is worded.
 *
 * Mirrors IssueKey in apps/journey_reviews/types.py.
 */
export type ReviewIssueKey =
  | 'placeholder_content'
  | 'unfinished_content'
  | 'raw_token_in_copy'
  | 'wrong_field_mapped'
  | 'unclear_field_label'
  | 'typo'
  | 'path_ends_on_wait'
  | 'trigger_not_configured'
  | 'trigger_inactive'
  | 'dead_end_branch'
  | 'unused_attribute'
  | 'unused_output';

export type ReviewFindingTarget = {
  kind: ReviewTargetKind;
  id: string | null;
  label: string;
};

export type ReviewFinding = {
  severity: ReviewSeverity;
  category: ReviewFindingCategory;
  issueKey: ReviewIssueKey;
  // Which thing inside the target: a field, a token, a label. Empty means all of it.
  subject: string;
  // Null when source is 'deterministic': those findings are about shape, not copy.
  contentHash: string | null;
  title: string;
  description: string;
  suggestedFix: string;
  target: ReviewFindingTarget;
  source: 'deterministic' | 'ai';
  ignored: boolean;
  ignoredBy: CompanyUserMinimal | null;
  ignoredAt: string | null;
};

/** Ignore/reopen request body. The identity is category + targetId + issueKey + subject. */
export type FindingSignature = {
  category: ReviewFindingCategory;
  targetId: string | null;
  issueKey: ReviewIssueKey;
  subject: string;
  title: string;
  contentHash: string | null;
};

export type JourneyReviewStatus = 'in_progress' | 'success' | 'failed';

export type JourneyReview = {
  shortId: string;
  status: JourneyReviewStatus;
  findings: ReviewFinding[];
  partial: boolean;
  partialNote: string | null;
  error: string | null;
  dateCreated: string;
};

export const loadLatestJourneyReview = async (journeyId: string): Promise<JourneyReview | null> => {
  const response = await request.get<JourneyReview | ''>(`v1/journeys/${journeyId}/review/latest/`);
  if (response.status === 204 || !response.data) {
    return null;
  }
  return response.data as JourneyReview;
};

export type StartedJourneyReview = {
  websocketId: string;
  reviewShortId: string;
};

export const startJourneyReview = async (journeyId: string) => {
  const response = await request.post<StartedJourneyReview>(`v1/journeys/${journeyId}/review/`);
  return response.data;
};

export const cancelJourneyReview = async (journeyId: string) => {
  await request.post(`v1/journeys/${journeyId}/review/cancel/`);
};

// One request for the whole group: N requests each invalidate, and the first
// response back reverts the ones still in flight.
export const ignoreFindings = async (journeyId: string, signatures: FindingSignature[]) => {
  await request.post(`v1/journeys/${journeyId}/review/ignored/`, signatures);
};

export const restoreFindings = async (journeyId: string, signatures: FindingSignature[]) => {
  await request.delete(`v1/journeys/${journeyId}/review/ignored/`, { data: signatures });
};
